import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import StepDialog from "../components/StepDialog";
import {
  FileText, Plus, CheckCircle2, Clock, Eye, Send, Shield, AlertTriangle,
  FileCheck, History, Target, Search, BarChart3, Users, Building, Activity,
  BookOpen, ClipboardCheck, RefreshCw, Info, Sparkles, ChevronRight, ChevronDown,
  Wand2, Settings, Check, X, Download, Trash2, Zap,
} from "lucide-react";

const POLICY_TYPES = [
  { value: "bcm_policy", label: "Business Continuity Management Policy", icon: Shield, phase: "PLAN", clause: "5.2" },
  { value: "bia_policy", label: "Business Impact Analysis Policy", icon: Search, phase: "PLAN", clause: "8.2.2" },
  { value: "risk_assessment_policy", label: "Risk Assessment Policy", icon: AlertTriangle, phase: "PLAN", clause: "8.2.3" },
  { value: "threat_profiling_policy", label: "Threat Profiling Policy", icon: Target, phase: "PLAN", clause: "8.2.3" },
  { value: "asset_management_policy", label: "Asset & Resource Management Policy", icon: Building, phase: "PLAN", clause: "8.3" },
  { value: "stakeholder_policy", label: "Stakeholder Management Policy", icon: Users, phase: "PLAN", clause: "4.2" },
  { value: "recovery_strategy_policy", label: "Recovery Strategy Policy", icon: FileCheck, phase: "DO", clause: "8.3" },
  { value: "crisis_management_policy", label: "Crisis Management Policy", icon: AlertTriangle, phase: "DO", clause: "8.4.2" },
  { value: "incident_response_policy", label: "Incident Response Policy", icon: Clock, phase: "DO", clause: "8.4.3" },
  { value: "communication_policy", label: "Crisis Communication Policy", icon: FileText, phase: "DO", clause: "8.4.3" },
  { value: "training_policy", label: "Training & Awareness Policy", icon: BookOpen, phase: "DO", clause: "7.2" },
  { value: "exercise_testing_policy", label: "Exercise & Testing Policy", icon: Activity, phase: "CHECK", clause: "8.5" },
  { value: "internal_audit_policy", label: "Internal Audit Policy", icon: ClipboardCheck, phase: "CHECK", clause: "9.2" },
  { value: "performance_evaluation_policy", label: "Performance Evaluation Policy", icon: BarChart3, phase: "CHECK", clause: "9.1" },
  { value: "continual_improvement_policy", label: "Continual Improvement Policy", icon: RefreshCw, phase: "ACT", clause: "10.2" },
  { value: "management_review_policy", label: "Management Review Policy", icon: Users, phase: "ACT", clause: "9.3" },
];

const PHASES = [
  { key: "PLAN", label: "PLAN", color: "bg-blue-600", textColor: "text-blue-700", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
  { key: "DO", label: "DO", color: "bg-emerald-600", textColor: "text-emerald-700", bgColor: "bg-emerald-50", borderColor: "border-emerald-200" },
  { key: "CHECK", label: "CHECK", color: "bg-amber-600", textColor: "text-amber-700", bgColor: "bg-amber-50", borderColor: "border-amber-200" },
  { key: "ACT", label: "ACT", color: "bg-purple-600", textColor: "text-purple-700", bgColor: "bg-purple-50", borderColor: "border-purple-200" },
];

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const FREQ_OPTIONS = [
  { value: "quarterly", label: "Quarterly" },
  { value: "bi-annual", label: "Bi-Annual" },
  { value: "annual", label: "Annual" },
  { value: "biennial", label: "Every 2 Years" },
];

export default function PoliciesPage() {
  const { api, user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewMode, setViewMode] = useState("summary");
  // AI Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardTemplate, setWizardTemplate] = useState(null);
  const [wizardConfig, setWizardConfig] = useState({ scope: "", owner_role: "", review_frequency: "annual", org_context: "", additional_instructions: "" });
  const [wizardGenerating, setWizardGenerating] = useState(false);
  const [wizardResult, setWizardResult] = useState(null);
  const [wizardSaving, setWizardSaving] = useState(false);
  const isBCM = user?.role === "bcm_manager" || user?.role === "admin";

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [policiesRes, statsRes, templatesRes] = await Promise.all([
        api.get("/policies/"),
        api.get("/policies/dashboard/stats"),
        api.get("/policies/templates"),
      ]);
      setPolicies(policiesRes.data || []);
      setStats(statsRes.data || {});
      const td = templatesRes.data?.templates || templatesRes.data || [];
      setTemplates(Array.isArray(td) ? td : []);
    } catch (error) {
      console.error("Failed to fetch:", error);
      setPolicies([]); setStats({}); setTemplates([]);
    } finally { setLoading(false); }
  };

  // ── AI Wizard Handlers ──
  const openWizard = (templateId) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;
    setWizardTemplate(tmpl);
    setWizardConfig({ scope: tmpl.default_scope || "", owner_role: tmpl.default_owner || "", review_frequency: tmpl.default_frequency || "annual", org_context: "", additional_instructions: "" });
    setWizardResult(null);
    setWizardStep(0);
    setWizardOpen(true);
  };

  const generatePolicy = async () => {
    setWizardGenerating(true);
    try {
      const res = await api.post("/policies/ai-generate", {
        template_id: wizardTemplate.id,
        scope: wizardConfig.scope,
        owner_role: wizardConfig.owner_role,
        review_frequency: wizardConfig.review_frequency,
        org_context: wizardConfig.org_context || undefined,
        additional_instructions: wizardConfig.additional_instructions || undefined,
      });
      setWizardResult(res.data);
      setWizardStep(2);
      toast.success("Policy generated successfully");
    } catch (err) {
      console.error("AI generation failed:", err);
      toast.error(extractErrorMessage(err, "AI generation failed. Please try again."));
    } finally { setWizardGenerating(false); }
  };

  const saveGeneratedPolicy = async () => {
    if (!wizardResult) return;
    setWizardSaving(true);
    try {
      await api.post("/policies/", {
        title: wizardResult.title,
        policy_type: wizardTemplate.id,
        description: wizardTemplate.description,
        content: wizardResult.full_text,
        summary_text: wizardResult.summary_text,
        functional_narrative: wizardResult.functional_narrative,
        iso_clause: wizardResult.iso_clause,
        scope: wizardResult.scope,
        review_frequency: wizardResult.review_frequency,
        owner: user.id,
      });
      toast.success(`"${wizardResult.title}" saved as draft`);
      setWizardOpen(false);
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save policy"));
    } finally { setWizardSaving(false); }
  };

  // ── Quick Create from Template (no AI) ──
  const quickCreateFromTemplate = async (templateId) => {
    try {
      await api.post(`/policies/from-template/${templateId}`);
      toast.success("Policy created from template");
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to create policy"));
    }
  };

  // ── Policy Actions ──
  const handleSubmitForReview = async (policyId) => {
    try {
      await api.post(`/policies/${policyId}/submit-for-review`);
      toast.success("Policy submitted for review");
      fetchData();
    } catch (err) { toast.error("Failed to submit"); }
  };

  const handleApprove = async (policyId) => {
    try {
      await api.post(`/policies/${policyId}/approve`, { comments: "" });
      toast.success("Policy approved");
      fetchData();
    } catch (err) { toast.error("Failed to approve"); }
  };

  const handleDelete = async (policyId) => {
    try {
      await api.delete(`/policies/${policyId}`);
      toast.success("Policy deleted");
      fetchData();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const handleRequestAcknowledgments = async (policyId) => {
    try {
      const res = await api.post(`/policies/${policyId}/request-acknowledgments`);
      toast.success(`Acknowledgment requests sent to ${res.data.total_requests} users`);
    } catch (err) { toast.error("Failed to send acknowledgment requests"); }
  };

  const viewPolicy = (policy) => {
    setSelectedPolicy(policy);
    setViewMode("summary");
    setShowViewDialog(true);
  };

  const getPolicyForTemplate = (templateId) => policies.find(p => p.policy_type === templateId);
  const getPhaseConfig = (phaseKey) => PHASES.find(p => p.key === phaseKey);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policies-page">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Badge className="bg-sky-100 text-sky-700 text-sm px-3 py-1">PP1</Badge>
            <Badge variant="outline" className="text-xs">ISO 22301 Clause 5.2</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">ISO 22301 Policy Library</h1>
          <p className="text-slate-600 mt-1">
            16 core policies aligned with ISO 22301. Use the <span className="text-sky-700 font-semibold">AI Wizard</span> to generate comprehensive, module-aware policy documents.
          </p>
        </div>
        <a
          href="/bcm-policy"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-sky-700 to-teal-600 text-white text-sm font-medium hover:from-sky-800 hover:to-teal-700 transition"
          data-testid="launch-policy-wizard-btn"
        >
          Launch Policy Wizard →
        </a>
      </div>

      {/* ── Stats ── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Coverage", value: `${stats.coverage || 0}/16`, sub: `${stats.coverage_score || 0}%`, icon: Shield, color: "text-sky-700", bg: "bg-sky-50" },
            { label: "Approved", value: stats.approved || 0, sub: "Active", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Under Review", value: stats.under_review || 0, sub: "Pending", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Draft", value: stats.draft || 0, sub: "In progress", icon: FileText, color: "text-slate-600", bg: "bg-slate-50" },
          ].map(({ label, value, sub, icon: Icon, color, bg }) => (
            <Card key={label} className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{value}</p>
                  <p className="text-xs text-slate-500">{label} &middot; {sub}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Policy Library Grid by Phase ── */}
      {PHASES.map(phase => {
        const phasePolicies = POLICY_TYPES.filter(t => t.phase === phase.key);
        return (
          <Card key={phase.key} className={`border ${phase.borderColor}`} data-testid={`phase-${phase.key.toLowerCase()}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded ${phase.color} flex items-center justify-center`}>
                  <span className="text-white text-xs font-bold">{phase.label[0]}</span>
                </div>
                <div>
                  <CardTitle className={`text-base ${phase.textColor}`}>{phase.label} Phase</CardTitle>
                  <CardDescription className="text-xs">
                    {phase.key === "PLAN" && "ISO Clauses 4-6 — Strategic Intent"}
                    {phase.key === "DO" && "ISO Clauses 7-8 — Operational Accountability"}
                    {phase.key === "CHECK" && "ISO Clause 9 — Oversight & Verification"}
                    {phase.key === "ACT" && "ISO Clause 10 — Strategic Realignment"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {phasePolicies.map(type => {
                  const Icon = type.icon;
                  const existing = getPolicyForTemplate(type.value);
                  const hasPolicy = !!existing;
                  return (
                    <div
                      key={type.value}
                      className={`rounded-lg border p-4 transition-all ${
                        hasPolicy
                          ? "bg-white border-emerald-200 shadow-sm"
                          : `${phase.bgColor} ${phase.borderColor} hover:shadow-sm`
                      }`}
                      data-testid={`policy-card-${type.value}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 flex-shrink-0 ${hasPolicy ? "text-emerald-600" : phase.textColor}`} />
                          <Badge variant="outline" className="text-[10px] font-mono">{type.clause}</Badge>
                        </div>
                        {hasPolicy && (
                          <Badge className={`text-[10px] ${STATUS_COLORS[existing.status] || STATUS_COLORS.draft}`}>
                            {existing.status === "approved" ? "Approved" : existing.status === "under_review" ? "Review" : "Draft"}
                          </Badge>
                        )}
                        {!hasPolicy && <div className="w-2 h-2 rounded-full bg-slate-300" />}
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 leading-tight mb-1">{type.label}</h3>
                      {hasPolicy ? (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => viewPolicy(existing)} data-testid={`view-${type.value}`}>
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                          {isBCM && existing.status === "draft" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => handleSubmitForReview(existing.id)}>
                              <Send className="w-3 h-3 mr-1" /> Submit
                            </Button>
                          )}
                          {isBCM && existing.status === "under_review" && (
                            <Button size="sm" className="h-7 text-xs px-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprove(existing.id)}>
                              <Check className="w-3 h-3 mr-1" /> Approve
                            </Button>
                          )}
                          {isBCM && existing.status === "approved" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => handleRequestAcknowledgments(existing.id)} data-testid={`ack-${type.value}`}>
                              <Users className="w-3 h-3 mr-1" /> Acknowledge
                            </Button>
                          )}
                          {isBCM && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-red-500 hover:text-red-700" onClick={() => handleDelete(existing.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ) : isBCM ? (
                        <div className="mt-3 flex gap-1.5">
                          <Button
                            size="sm"
                            className="h-7 text-xs px-2.5 bg-sky-700 hover:bg-sky-800"
                            onClick={() => openWizard(type.value)}
                            data-testid={`ai-wizard-${type.value}`}
                          >
                            <Wand2 className="w-3 h-3 mr-1" /> AI Wizard
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => quickCreateFromTemplate(type.value)} data-testid={`quick-create-${type.value}`}>
                            <Plus className="w-3 h-3 mr-1" /> Template
                          </Button>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 mt-2 italic">Not yet created</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* ── AI Wizard Dialog ── */}
      <StepDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        title={`AI Policy Wizard — ${wizardTemplate?.title || ""}`}
        steps={[
          { id: "configure", title: "Configure", subtitle: `${wizardTemplate?.title || ""} · ISO ${wizardTemplate?.iso_clause || ""}`, isComplete: !!wizardConfig.scope },
          { id: "generating", title: "Generating", subtitle: "AI is synthesizing your policy document", isComplete: !!wizardResult, isDisabled: !wizardGenerating && !wizardResult },
          { id: "review", title: "Review & Save", subtitle: "Review the generated policy and save as draft", isComplete: false, isDisabled: !wizardResult },
        ]}
        currentStep={wizardStep}
        onStepChange={(step) => {
          if (step === 1 && !wizardGenerating && !wizardResult) return;
          if (step === 2 && !wizardResult) return;
          setWizardStep(step);
        }}
        onSubmit={saveGeneratedPolicy}
        onCancel={() => setWizardOpen(false)}
        submitLabel={wizardSaving ? "Saving..." : "Save as Draft"}
        submitDisabled={wizardSaving || !wizardResult}
        saving={wizardSaving}
        canAdvance={wizardStep === 0}
        maxWidth="max-w-3xl"
      >
        {/* Step 0: Configure */}
        {wizardStep === 0 && wizardTemplate && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div className={`p-3 rounded-lg border ${getPhaseConfig(wizardTemplate.phase)?.bgColor} ${getPhaseConfig(wizardTemplate.phase)?.borderColor}`}>
              <p className="text-sm font-medium text-slate-700">{wizardTemplate.description}</p>
              <p className="text-xs text-slate-500 mt-1">Module: {wizardTemplate.module_reference}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs font-medium">Scope (What this policy covers)</Label>
                <Textarea
                  value={wizardConfig.scope}
                  onChange={(e) => setWizardConfig({ ...wizardConfig, scope: e.target.value })}
                  rows={2}
                  className="text-sm mt-1"
                  placeholder="All business units, departments, and personnel..."
                  data-testid="wizard-scope"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Policy Owner</Label>
                <Select value={wizardConfig.owner_role} onValueChange={(v) => setWizardConfig({ ...wizardConfig, owner_role: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BCM Manager">BCM Manager</SelectItem>
                    <SelectItem value="IT-DRP Manager">IT-DRP Manager</SelectItem>
                    <SelectItem value="Process Manager">Process Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Review Frequency</Label>
                <Select value={wizardConfig.review_frequency} onValueChange={(v) => setWizardConfig({ ...wizardConfig, review_frequency: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQ_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-medium">Organization Context <span className="text-slate-400">(Optional)</span></Label>
                <Textarea
                  value={wizardConfig.org_context}
                  onChange={(e) => setWizardConfig({ ...wizardConfig, org_context: e.target.value })}
                  rows={2}
                  className="text-sm mt-1"
                  placeholder="Industry type, regulatory requirements, key operations..."
                  data-testid="wizard-context"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-medium">Additional Instructions <span className="text-slate-400">(Optional)</span></Label>
                <Input
                  value={wizardConfig.additional_instructions}
                  onChange={(e) => setWizardConfig({ ...wizardConfig, additional_instructions: e.target.value })}
                  className="text-sm mt-1"
                  placeholder="Emphasize data privacy, focus on cloud operations, etc."
                  data-testid="wizard-instructions"
                />
              </div>
            </div>
            {/* Functional Narrative Preview */}
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-3.5 h-3.5 text-sky-700" />
                <span className="text-xs font-semibold text-slate-700">Pre-Written Module Logic (will be enhanced by AI)</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">{wizardTemplate.functional_narrative}</p>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={() => { setWizardStep(1); generatePolicy(); }}
                className="bg-sky-700 hover:bg-sky-800"
                data-testid="wizard-generate-btn"
              >
                <Sparkles className="w-4 h-4 mr-1.5" /> Generate with AI
              </Button>
            </div>
          </div>
        )}

        {/* Step 1: Generating */}
        {wizardStep === 1 && (
          <div className="flex flex-col items-center justify-center py-16 space-y-4" data-testid="wizard-step-2">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-sky-700 animate-pulse" />
              </div>
              <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-sky-300 border-t-indigo-600 animate-spin" />
            </div>
            <p className="text-lg font-semibold text-slate-900">Synthesizing Policy Document</p>
            <p className="text-sm text-slate-500 text-center max-w-sm">
              Merging ISO 22301 requirements with ResiliencyHUB module logic and your organizational context...
            </p>
          </div>
        )}

        {/* Step 2: Review & Save */}
        {wizardStep === 2 && wizardResult && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Policy Generated Successfully</p>
                <p className="text-xs text-emerald-600">Review the content below, then save as draft</p>
              </div>
            </div>
            {/* Toggle: Summary / Full Text */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
              <button
                onClick={() => setViewMode("summary")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === "summary" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                data-testid="toggle-summary"
              >Summary</button>
              <button
                onClick={() => setViewMode("full")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === "full" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                data-testid="toggle-full"
              >Full Document</button>
              <button
                onClick={() => setViewMode("narrative")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === "narrative" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                data-testid="toggle-narrative"
              >Module Logic</button>
            </div>
            {/* Content Area */}
            <div className="border rounded-lg bg-white p-4 min-h-[200px] max-h-[400px] overflow-y-auto">
              {viewMode === "summary" && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">{wizardResult.title}</h3>
                  <Badge variant="outline" className="text-xs font-mono mb-3">ISO {wizardResult.iso_clause}</Badge>
                  <p className="text-sm text-slate-700 leading-relaxed mb-4">{wizardResult.summary_text}</p>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="p-2 bg-slate-50 rounded"><span className="text-slate-500">Scope:</span> <span className="text-slate-700">{wizardResult.scope}</span></div>
                    <div className="p-2 bg-slate-50 rounded"><span className="text-slate-500">Owner:</span> <span className="text-slate-700">{wizardResult.owner}</span></div>
                    <div className="p-2 bg-slate-50 rounded"><span className="text-slate-500">Review:</span> <span className="text-slate-700">{wizardResult.review_frequency}</span></div>
                    <div className="p-2 bg-slate-50 rounded"><span className="text-slate-500">Phase:</span> <span className="text-slate-700">{wizardResult.phase}</span></div>
                  </div>
                </div>
              )}
              {viewMode === "full" && (
                <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed text-sm">
                  {wizardResult.full_text}
                </div>
              )}
              {viewMode === "narrative" && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-sky-700" />
                    <h4 className="font-semibold text-slate-900">Module Functionality & Logic</h4>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {wizardResult.functional_narrative}
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setWizardStep(0)}>
                <RefreshCw className="w-4 h-4 mr-1" /> Regenerate
              </Button>
            </div>
          </div>
        )}
      </StepDialog>

      {/* ── View Policy Dialog ── */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedPolicy && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <DialogTitle className="text-lg">{selectedPolicy.title}</DialogTitle>
                  <Badge className={STATUS_COLORS[selectedPolicy.status] || STATUS_COLORS.draft}>
                    {selectedPolicy.status}
                  </Badge>
                  {selectedPolicy.iso_clause && <Badge variant="outline" className="text-xs font-mono">ISO {selectedPolicy.iso_clause}</Badge>}
                  {selectedPolicy.ai_generated && <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]">AI Generated</Badge>}
                  <Badge variant="outline" className="text-[10px]">v{selectedPolicy.version}</Badge>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto mt-3 space-y-4">
                {/* Toggle */}
                <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
                  <button onClick={() => setViewMode("summary")} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === "summary" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                    Summary
                  </button>
                  <button onClick={() => setViewMode("full")} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === "full" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                    Full Document
                  </button>
                  {selectedPolicy.functional_narrative && (
                    <button onClick={() => setViewMode("narrative")} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === "narrative" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                      Module Logic
                    </button>
                  )}
                </div>
                {/* Meta */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  {selectedPolicy.scope && <div className="p-2 bg-slate-50 rounded border"><span className="text-slate-400 block">Scope</span>{selectedPolicy.scope}</div>}
                  {selectedPolicy.review_frequency && <div className="p-2 bg-slate-50 rounded border"><span className="text-slate-400 block">Review</span>{selectedPolicy.review_frequency}</div>}
                  {selectedPolicy.created_at && <div className="p-2 bg-slate-50 rounded border"><span className="text-slate-400 block">Created</span>{new Date(selectedPolicy.created_at).toLocaleDateString()}</div>}
                  {selectedPolicy.approved_by && <div className="p-2 bg-slate-50 rounded border"><span className="text-slate-400 block">Approved By</span>{selectedPolicy.approved_by}</div>}
                </div>
                {/* Content */}
                <div className="border rounded-lg bg-white p-4 min-h-[200px]">
                  {viewMode === "summary" && (
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {selectedPolicy.summary_text || selectedPolicy.description || "No summary available."}
                    </p>
                  )}
                  {viewMode === "full" && (
                    <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed text-sm">
                      {selectedPolicy.content || "No content available."}
                    </div>
                  )}
                  {viewMode === "narrative" && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-sky-700" />
                        <h4 className="font-semibold text-slate-900 text-sm">Module Functionality & Logic</h4>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {selectedPolicy.functional_narrative || "No functional narrative available."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-4 pt-4 border-t gap-2">
                <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
                {isBCM && selectedPolicy.status === "approved" && (
                  <Button size="sm" onClick={() => { handleRequestAcknowledgments(selectedPolicy.id); setShowViewDialog(false); }}>
                    <Users className="w-4 h-4 mr-1" /> Request Acknowledgments
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
